import { CoolingWaterSystemVm, EngineReportDetails } from '@navstation-portal/reporting/data-model/engine-report';
import { Units } from '@navstation-portal/shared/data-model/ui';
import { TooltipSeverity, getHardValidation, getRangeTooltip, isOutOfRange } from '../range-tooltip.utils';

const RANGE_LIMITS = {
  cwTempBeforeLoCool: { min: 0 },
  cwTempAfterLoCool: { min: 0 },
} as const;

const SOFT_RANGE_LIMITS = {
  cwTempBeforeAirCooler: { min: 20, max: 60 },
  cwTempAfterAirCooler: { min: 5, max: 80 },
} as const;

const HARD_RANGE_LIMITS = {
  htFwTempInlet: { max: 90 },
  htFwPressureInlet: { min: 0, max: 5 },
  cwTempBeforeAirCooler: { min: 0, max: 200 },
  cwTempAfterAirCooler: { min: 0, max: 60 },
} as const;

export function buildCoolingWaterSystemVm(details: EngineReportDetails | undefined): CoolingWaterSystemVm {
  const cwTempBeforeLoCoolInvalid =
    details?.cwTempBeforeLoCool == null || isOutOfRange(details.cwTempBeforeLoCool, RANGE_LIMITS.cwTempBeforeLoCool);
  const cwTempBeforeLoCoolInvalidTooltip = `Cooling Water Temperature must be ≥ ${RANGE_LIMITS.cwTempBeforeLoCool.min}°C`;

  const cwTempAfterLoCoolInvalid =
    details?.cwTempAfterLoCool == null || isOutOfRange(details.cwTempAfterLoCool, RANGE_LIMITS.cwTempAfterLoCool);
  const cwTempAfterLoCoolInvalidTooltip = `Cooling Water Temperature must be ≥ ${RANGE_LIMITS.cwTempAfterLoCool.min}°C`;

  const cwTempBeforeAirCoolerValidation = getHardValidation(
    details?.cwTempBeforeAirCooler,
    HARD_RANGE_LIMITS.cwTempBeforeAirCooler,
    'CW Temperature Before Air Cooler',
    Units.degreesCelsius,
    true
  );

  const cwTempAfterAirCoolerValidation = getHardValidation(
    details?.cwTempAfterAirCooler,
    HARD_RANGE_LIMITS.cwTempAfterAirCooler,
    'CW Temperature After Air Cooler',
    Units.degreesCelsius,
    true
  );

  // Hard error takes precedence over the soft warning when both would otherwise show.
  const cwTempBeforeAirCoolerWarning =
    !cwTempBeforeAirCoolerValidation.invalid &&
    isOutOfRange(details?.cwTempBeforeAirCooler, SOFT_RANGE_LIMITS.cwTempBeforeAirCooler);
  const cwTempBeforeAirCoolerWarningTooltip = getRangeTooltip(
    'CW Temp Before Air Cooler',
    SOFT_RANGE_LIMITS.cwTempBeforeAirCooler,
    Units.degreesCelsius,
    TooltipSeverity.Warning
  );

  const cwTempAfterAirCoolerWarning =
    !cwTempAfterAirCoolerValidation.invalid &&
    isOutOfRange(details?.cwTempAfterAirCooler, SOFT_RANGE_LIMITS.cwTempAfterAirCooler);
  const cwTempAfterAirCoolerWarningTooltip = getRangeTooltip(
    'CW Temp After Air Cooler',
    SOFT_RANGE_LIMITS.cwTempAfterAirCooler,
    Units.degreesCelsius,
    TooltipSeverity.Warning
  );

  const htFwTempInletValidation = getHardValidation(
    details?.htFwTempInlet,
    HARD_RANGE_LIMITS.htFwTempInlet,
    'HT FW Temperature Inlet',
    Units.degreesCelsius
  );

  const htFwPressureInletValidation = getHardValidation(
    details?.htFwPressureInlet,
    HARD_RANGE_LIMITS.htFwPressureInlet,
    'HT FW Pressure Inlet',
    Units.bar
  );

  return {
    cwTempBeforeLoCoolInvalid,
    cwTempBeforeLoCoolInvalidTooltip,
    cwTempAfterLoCoolInvalid,
    cwTempAfterLoCoolInvalidTooltip,
    cwTempBeforeAirCoolerWarning,
    cwTempBeforeAirCoolerWarningTooltip,
    cwTempAfterAirCoolerWarning,
    cwTempAfterAirCoolerWarningTooltip,
    htFwTempInletInvalid: htFwTempInletValidation.invalid,
    htFwTempInletInvalidTooltip: htFwTempInletValidation.tooltip,
    htFwPressureInletInvalid: htFwPressureInletValidation.invalid,
    htFwPressureInletInvalidTooltip: htFwPressureInletValidation.tooltip,
    cwTempBeforeAirCoolerInvalid: cwTempBeforeAirCoolerValidation.invalid,
    cwTempBeforeAirCoolerInvalidTooltip: cwTempBeforeAirCoolerValidation.tooltip,
    cwTempAfterAirCoolerInvalid: cwTempAfterAirCoolerValidation.invalid,
    cwTempAfterAirCoolerInvalidTooltip: cwTempAfterAirCoolerValidation.tooltip,
  };
}
